import util from '../lib/util';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";
function getCurrentUserId() { return sessionStorage.getItem('userId'); }
function getCurrentFirmId() { return sessionStorage.getItem('firmId'); }
class CaseTypeStage extends Base implements EntityType {
    caseTypeStageId: uuid;
    caseTypeId: uuid;
    name: string;
    description?: string | null;
    sortOrder: number;
    isDefault: boolean;
    color?: string | null;
    templateTaskIds: uuid[];
    constructor(doc: typeof CaseTypeStage | any) {
        super(doc);
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: "C",
            entityId: null,
            entityType: null,
            caseTypeStageId: genUUID(),
            caseTypeId: null,
            name: null,
            sortOrder: null,
        };
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || "caseTypeStage";
        // @ts-ignore
        this.caseTypeStageId = doc._caseTypeStageId || doc.caseTypeStageId || doc.casetypestageid || genUUID();
        // @ts-ignore
        this.caseTypeId = doc._caseTypeId || doc.caseTypeId || doc.casetypeid || null;
        // @ts-ignore
        this.name = doc._name || doc.name || null;
        // @ts-ignore
        this.description = doc._description || doc.description || null;
        // @ts-ignore
        this.sortOrder = util.coalesce(util.parseInt(doc._sortOrder), util.parseInt(doc.sortOrder), util.parseInt(doc.sortorder), null);
        // @ts-ignore
        this.isDefault = util.defaultBoolean([doc.isDefault, doc._isDefault, doc.isdefault, true]);
        // @ts-ignore
        this.color = doc._color || doc.color || null;
        // @ts-ignore
        this.templateTaskIds = doc._templateTaskIds || doc.templateTaskIds || doc.templatetaskids || [];
    }
    get id(): uuid { return this.caseTypeStageId; }
    addTemplateTaskId(templateTaskId: uuid): uuid {
        if (!templateTaskId) throw new ValidationError('No nulls allowed in an array!');
        if (!this.templateTaskIds) this.templateTaskIds = [];
        this.templateTaskIds.push(templateTaskId);
        return templateTaskId;
    }
    updateTemplateTaskId(templateTaskId: uuid) {
        if (!this.templateTaskIds) this.templateTaskIds = [];
        const index = this.templateTaskIds.findIndex(item => item === templateTaskId);
        if (index !== -1) { this.templateTaskIds[index] = templateTaskId; }
        else this.addTemplateTaskId(templateTaskId);
    }
    removeTemplateTaskId(templateTaskId: uuid) {
        if (!this.templateTaskIds) return;
        let foundItems = this.templateTaskIds?.filter(item => item === templateTaskId);
        if (foundItems && foundItems.length > 0) {
            this.templateTaskIds = this.templateTaskIds.filter(item => !(item === templateTaskId));
        }
    }
    abandonTemplateTaskIds() { this.templateTaskIds = []; }
    static override getPkFields(): SchemaField[] { return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique id for this case type stage"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","comment":"Associated firm"}]; }
    toLowerCase() { return this.caseTypeStageId; }
    static override getNonRefArrayFields(): string[] { return ["templateTaskIds"]; }
    static override getDefaultEntries(): CaseTypeStage[] {
        return [];
    }
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
    entity: 'caseTypeStage',
    dataArea: 'Firm Settings',
    attachedTo: {parents: ['caseType'], via: 'caseTypeId', onDelete: 'delete', onReplace: 'move'},
    table: 'CASE_TYPE_STAGE',
    comment: `A reusable stage template defined on a case type, representing one step in a case's lifecycle (e.g., Intake, Discovery, Trial). When a new case is created, the firm selects which stages to include; stages marked isDefault are pre-selected. Each stage carries a display color, a sort order for sequential progression, and optional task templates that are auto-created when a case enters that stage. At case-creation time, selected stages are instantiated as matterStage records.`,
    fields: {
        caseTypeStageId: {
            type: 'uuid',
            required: true,
            pk: true,
            default: '__UUID__',
            comment: 'Unique id for this case type stage'
        },

        /**
         * The case type this stage belongs to
         */
        caseTypeId: {
            type: 'uuid',
            required: true,
            entityType: 'caseType',
            comment: 'The case type this stage belongs to'
        },

        /**
         * Display name for the stage, e.g. "Discovery", "Mediation", "Trial Prep"
         */
        name: {
            type: 'string',
            required: true,
            comment: 'Display name for this stage'
        },

        /**
         * Optional description of what happens during this stage
         */
        description: {
            type: 'string',
            comment: 'Description of what happens in this stage'
        },

        /**
         * Display order within the case type. Stages are shown left-to-right in this order.
         */
        sortOrder: {
            type: 'int',
            required: true,
            comment: 'Display order within the case type (lower = earlier)'
        },

        /**
         * If true, this stage is pre-selected when creating a new case of this type.
         * Users can still uncheck it. If false, the stage is optional and must be opted into.
         */
        isDefault: {
            type: 'boolean',
            default: true,
            comment: 'Should this stage be included by default when creating a new case?'
        },

        /**
         * Hex color for the Kanban column header and progress bar indicator
         */
        color: {
            type: 'string',
            comment: 'Hex color code for visual display (e.g. #4CAF50)'
        },

        /**
         * Task templates to auto-create when a case enters this stage
         */
        templateTaskIds: {
            type: 'uuid[]',
            entityType: 'templateTask',
            comment: 'Task templates to instantiate when a case enters this stage'
        }
    },
    indexes: [
        {name: 'notDeleted', fn: s => s.status !== 'D'}
    ],
    maps: [
        {
            name: 'caseTypeId',
            keysFn: s => s.caseTypeId,
            presentFn: (s, k) => s.caseTypeId === k && s.status !== 'D'
        },
        {
            name: 'templateTaskIds',
            keysFn: s => s.templateTaskIds,
            presentFn: (s, k) => s.templateTaskIds?.includes(k)
        }
    ]
});
    }
}
export default CaseTypeStage;
